'use client';

/**
 * Shimmer — adapted from AI Elements (https://elements.ai-sdk.dev/components/shimmer)
 *
 * Uses Aura theme colors instead of shadcn `--color-*` CSS variables.
 */

import { type MotionProps, motion } from 'motion/react';
import { type CSSProperties, type JSX, memo, useMemo } from 'react';

import { cn } from '../../../lib/utils/cn';

type MotionHTMLProps = MotionProps & Record<string, unknown>;

const motionComponentCache = new Map<
  keyof JSX.IntrinsicElements,
  React.ComponentType<MotionHTMLProps>
>();

const getMotionComponent = (element: keyof JSX.IntrinsicElements) => {
  let component = motionComponentCache.get(element);
  if (!component) {
    component = motion.create(element);
    motionComponentCache.set(element, component);
  }
  return component;
};

export interface TextShimmerProps {
  children: string;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  duration?: number;
  spread?: number;
}

const ShimmerComponent = ({
  children,
  as: Component = 'p',
  className,
  duration = 2,
  spread = 4,
}: TextShimmerProps) => {
  const MotionComponent = getMotionComponent(Component);

  /**
   * Uncapped (length × spread) makes the highlight wider than the text mask → entire clip fills
   * with the bright stop → invisible on white backgrounds (e.g. long copy in Storybook).
   */
  const spreadPx = useMemo(() => {
    const raw = (children?.length ?? 0) * spread;
    return Math.min(Math.max(raw, 10), 28);
  }, [children, spread]);

  return (
    <MotionComponent
      animate={{ backgroundPosition: '0% center' }}
      className={cn(
        'relative inline-block bg-[length:250%_100%,auto] bg-clip-text [background-repeat:no-repeat,padding-box] text-transparent [-webkit-background-clip:text]',
        className,
      )}
      initial={{ backgroundPosition: '100% center' }}
      style={
        {
          '--spread': `${spreadPx}px`,
          // Literal rgbs so glyphs + sweep always paint (theme vars can be missing in some Storybook iframes)
          backgroundImage: `linear-gradient(90deg, transparent calc(50% - var(--spread)), rgb(255, 255, 255), transparent calc(50% + var(--spread))), linear-gradient(rgb(26, 44, 64), rgb(26, 44, 64))`,
        } as CSSProperties
      }
      transition={{
        duration,
        ease: 'linear',
        repeat: Number.POSITIVE_INFINITY,
      }}>
      {children}
    </MotionComponent>
  );
};

/** Text loading shimmer (AI Elements). Named `TextShimmer` to avoid clashing with the skeleton `Shimmer` in `@bridebook/aura`. */
export const TextShimmer = memo(ShimmerComponent);
